// ─────────────────────────────────────────────────────────────────────────────
// utils/solutionUtils.ts
// Pure helper functions — no React, no side-effects, fully unit-testable
// ─────────────────────────────────────────────────────────────────────────────

import { ENVIRONMENT_TYPE } from '../../../config/env';
import { createLabelWithUsername } from '../../../utility/userUtils';
import { ApprovalStatusConfig, CardData, NormalizedAgentData } from './types';

// ─── Timestamp / Sync Helpers ─────────────────────────────────────────────────

/**
 * Determines whether a re-sync is needed by comparing the enterprise-tech
 * `updated_at` timestamp against the agent's `updated_at` timestamp.
 * Returns `true` when enterprise tech is newer (i.e. the agent is stale).
 * Returns `false` on any error to prevent sync loops.
 */
export const checkIfSyncNeeded = async (
  enterpriseTechResponse: any,
  agent: any
): Promise<boolean> => {
  try {
    if (!agent?.updated_at) return true;
    if (!enterpriseTechResponse?.updated_at) return false;

    const toUtcMs = (ts: string) => new Date(ts.endsWith('Z') ? ts : ts + 'Z').getTime();

    const agentMs = toUtcMs(agent.updated_at);
    const techMs = toUtcMs(enterpriseTechResponse.updated_at);

    if (techMs > agentMs) {
      console.log('🔄 Sync needed: Enterprise Technology is newer than Agent');
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error comparing timestamps:', error);
    return false; // default to NO-sync to prevent loops
  }
};

// ─── Payload Builders ─────────────────────────────────────────────────────────

/**
 * Builds the request payload shared by both sync API calls.
 * Optionally merges `additionalPayload` on top of the base shape.
 */
export const buildSyncPayload = (
  aiSolutionData: any,
  enterpriseTechResponse: any,
  additionalPayload?: any,
  processType?: 'custom' | 'standard' | string
) => {
  const { company_name, description, industry_data } = enterpriseTechResponse;
  const {
    id,
    roi,
    impact,
    benefits,
    complexity,
    data_sources,
    key_requirements,
    opportunity_name,
  } = aiSolutionData || {};

  const basePayload = {
    company_info: { company_name, description },
    technology: industry_data,
    aiSolution: {
      solution_id: id,
      roi,
      impact,
      benefits,
      complexity,
      data_sources,
      key_requirements,
      opportunity_name,
      description: aiSolutionData?.description || '',
    },
    is_from_process_xplr:
      aiSolutionData?.ideation_xplr === true || aiSolutionData?.subprocess_type === 'custom'
        ? false
        : window.location.pathname.includes('process-xplr') || processType === 'standard',
    label: createLabelWithUsername(aiSolutionData?.id || ''),
    organization_id: window?.localStorage?.getItem('current_organization') || '',
  };

  const extraFields = {
    llm_agent: aiSolutionData?.llm_agent,
    solution_enabler: aiSolutionData?.solution_enabler,
    diagram_id: aiSolutionData?.diagram_id,
    organization_id: localStorage.getItem('current_organization') || '',
    type: ENVIRONMENT_TYPE,
  };

  return additionalPayload
    ? { ...basePayload, ...additionalPayload, ...extraFields }
    : { ...basePayload, ...extraFields };
};

// ─── Response Mergers ─────────────────────────────────────────────────────────

/**
 * Merges two sync API responses, combining arrays (agent_classification,
 * application_in_use) and deeply merging ai_enablement objects.
 */
export const mergeSyncResponses = (firstResponse: any, secondResponse: any): any => ({
  ...firstResponse,
  ...secondResponse,
  ai_enablement: {
    ...(typeof firstResponse.ai_enablement === 'object' && firstResponse.ai_enablement
      ? firstResponse.ai_enablement
      : {}),
    ...(typeof secondResponse.ai_enablement === 'object' && secondResponse.ai_enablement
      ? secondResponse.ai_enablement
      : {}),
  },
  agent_classification: [
    ...(Array.isArray(firstResponse.agent_classification)
      ? firstResponse.agent_classification
      : []),
    ...(Array.isArray(secondResponse.agent_classification)
      ? secondResponse.agent_classification
      : []),
  ],
  application_in_use: [
    ...(Array.isArray(firstResponse.application_in_use) ? firstResponse.application_in_use : []),
    ...(Array.isArray(secondResponse.application_in_use) ? secondResponse.application_in_use : []),
  ],
});

/**
 * Safely merges agent-sync response data into an existing solution object
 * without touching non-agent fields (description, roi, etc.).
 * The agent section is fully owned by the sync response.
 */
export const mergeAgentSyncData = (baseData: any, syncResponse: any): any => {
  const source = baseData || {};

  const getAgentField = (fieldName: 'embedded' | 'extended' | 'custom', res: any) => {
    const fromMatrix = res?.ai_enablement?.enablement_matrix?.[fieldName];
    if (Array.isArray(fromMatrix) && fromMatrix.length > 0) return fromMatrix;

    const fromDirect = res?.[fieldName];
    if (Array.isArray(fromDirect) && fromDirect.length > 0) return fromDirect;

    if (
      fieldName === 'embedded' &&
      Array.isArray(res?.agent_classification) &&
      res.agent_classification.length > 0
    ) {
      return res.agent_classification;
    }

    return [];
  };

  const syncEnablement = syncResponse?.ai_enablement;
  const mergedAiEnablement = syncEnablement
    ? {
        ...syncEnablement,
        ...(syncEnablement.enablement_matrix
          ? { enablement_matrix: syncEnablement.enablement_matrix }
          : {}),
      }
    : undefined;

  return {
    ...source,
    agent: {
      ...source?.agent,
      ai_enablement:
        mergedAiEnablement !== undefined ? mergedAiEnablement : source?.agent?.ai_enablement,
      ai_solution: syncResponse?.application_in_use || syncResponse?.ai_solution || [],
      agent_classification: syncResponse?.agent_classification || [],
      embedded: getAgentField('embedded', syncResponse),
      extended: getAgentField('extended', syncResponse),
      custom: getAgentField('custom', syncResponse),
    },
  };
};

// ─── UI Config Helpers ────────────────────────────────────────────────────────

export const getApprovalStatusConfig = (status?: string | null): ApprovalStatusConfig => {
  if (!status) return { text: '', show: false };

  switch (String(status).trim().toLowerCase()) {
    case 'approval_pending':
      return {
        text: 'Approval pending',
        color: '#FF0505',
        bgColor: '#FFF5F2',
        borderColor: '#FF0505',
        show: true,
      };
    case 'approved':
      return {
        text: 'Approved',
        color: '#11953E',
        bgColor: '#DBFFE8',
        borderColor: '#11953E',
        show: true,
      };
    case 'solutioning':
      return {
        text: 'Solutioning',
        color: '#11953E',
        bgColor: '#DBFFE8',
        borderColor: '#11953E',
        show: true,
      };
    case 'ready_to_build':
      return {
        text: 'Ready to Build',
        color: '#626262',
        bgColor: 'rgba(169,169,169,0.16)',
        borderColor: '#626262',
        show: true,
      };
    case 'build':
      return {
        text: 'Build',
        color: '#11953E',
        bgColor: '#F3E8FF',
        borderColor: '#11953E',
        show: true,
      };
    default:
      return { text: '', show: false };
  }
};

export const getSvgPathForRoi = (type: string) => {
  switch (type) {
    case 'HIGH':
      return '/images/GreenDot.svg';
    case 'MEDIUM':
      return '/images/YellowDot.svg';
    case 'LOW':
      return '/images/RedDot.svg';
    default:
      return '/images/GreenDot.svg';
  }
};

export const getSvgPathForComplexity = (type: string) => {
  switch (type) {
    case 'HIGH':
      return '/images/RedDot.svg';
    case 'MEDIUM':
      return '/images/YellowDot.svg';
    case 'LOW':
      return '/images/GreenDot.svg';
    default:
      return '/images/GreenDot.svg';
  }
};

/**
 * Parses the solution_enabler field, which may arrive as a Python-style
 * list string e.g. "['value1', 'value2']".
 */
export const handleEnablerText = (input: any): string => {
  if (typeof input === 'string' && input.startsWith('[') && input.endsWith(']')) {
    return JSON.parse(input.replace(/'/g, '"')).join(', ');
  }
  return input;
};

// ─── Agent Data Normalizers ───────────────────────────────────────────────────

const DEFAULT_AGENT_DATA: NormalizedAgentData = { embedded: [], extended: [], custom: [] };

/**
 * Validates that a cached solution's agent data contains a complete enablement
 * structure (matrix or direct arrays with items).
 */
export const validateAgentDataCompleteness = (solutionData: any): boolean => {
  if (!solutionData?.agent) return false;
  const agent = solutionData.agent;

  const hasEnablementMatrix =
    agent?.ai_enablement?.enablement_matrix &&
    typeof agent.ai_enablement.enablement_matrix === 'object' &&
    (Array.isArray(agent.ai_enablement.enablement_matrix.embedded) ||
      Array.isArray(agent.ai_enablement.enablement_matrix.extended) ||
      Array.isArray(agent.ai_enablement.enablement_matrix.custom));

  const hasDirectArrays =
    (Array.isArray(agent.embedded) && agent.embedded.length > 0) ||
    (Array.isArray(agent.extended) && agent.extended.length > 0) ||
    (Array.isArray(agent.custom) && agent.custom.length > 0);

  const hasEmbeddedWithType =
    Array.isArray(agent.embedded) &&
    agent.embedded.length > 0 &&
    agent.embedded.some((item: any) => item?.implementation_type);

  return hasEnablementMatrix || hasDirectArrays || hasEmbeddedWithType;
};

/**
 * Checks whether agent enablement data exists in a solution object,
 * scanning the multiple possible structures the API may return.
 */
export const hasCachedEnablementData = (solutionData: any): boolean => {
  const agent = solutionData?.agent;
  return (
    (agent?.ai_enablement?.enablement_matrix &&
      (agent.ai_enablement.enablement_matrix.embedded?.length > 0 ||
        agent.ai_enablement.enablement_matrix.extended?.length > 0 ||
        agent.ai_enablement.enablement_matrix.custom?.length > 0)) ||
    (Array.isArray(agent?.embedded) && agent.embedded.length > 0) ||
    (Array.isArray(agent?.extended) && agent.extended.length > 0) ||
    (Array.isArray(agent?.custom) && agent.custom.length > 0) ||
    (Array.isArray(agent?.agent_classification) && agent.agent_classification.length > 0) ||
    (agent?.ai_enablement && typeof agent.ai_enablement === 'object')
  );
};

/**
 * Normalizes raw agent API data into the { embedded, extended, custom } shape
 * used throughout the UI.
 *
 * Handles two distinct data shapes:
 *   - ideation:      agent.ai_enablement.enablement_matrix.*
 *   - process-xplr: agent.embedded[] with implementation_type discrimination
 */
export const normalizeAgentData = (
  displayData: any,
  effectiveIsIdeation: boolean
): NormalizedAgentData => {
  if (!displayData) return DEFAULT_AGENT_DATA;

  const agent = displayData?.agent;
  if (!agent) return DEFAULT_AGENT_DATA;

  try {
    if (effectiveIsIdeation) {
      const enablementMatrix = agent?.ai_enablement?.enablement_matrix;
      if (enablementMatrix && typeof enablementMatrix === 'object') {
        const { embedded = [], extended = [], custom = [] } = enablementMatrix;
        return {
          embedded: Array.isArray(embedded) ? embedded : [],
          extended: Array.isArray(extended) ? extended : [],
          custom: Array.isArray(custom) ? custom : [],
        };
      }
      return DEFAULT_AGENT_DATA;
    }

    if (window.location.pathname.includes('process-xplr')) {
      const agentdata: NormalizedAgentData = { embedded: [], extended: [], custom: [] };

      const mapMatrixItem = (item: any): CardData => ({
        capability_name: item?.capability_name || item?.agent_name || '',
        description: item?.description || item?.justification || '',
      });

      // Priority 1 — enablement_matrix
      const enablementMatrix = agent?.ai_enablement?.enablement_matrix;
      if (enablementMatrix && typeof enablementMatrix === 'object') {
        const { embedded: em = [], extended: ex = [], custom: cu = [] } = enablementMatrix;

        if (Array.isArray(em) && em.length > 0)
          em.forEach((item: any) => {
            const m = mapMatrixItem(item);
            if (m.capability_name) agentdata.embedded.push(m);
          });

        if (Array.isArray(ex) && ex.length > 0)
          ex.forEach((item: any) => {
            const m = mapMatrixItem(item);
            if (m.capability_name) agentdata.extended.push(m);
          });

        if (Array.isArray(cu) && cu.length > 0)
          cu.forEach((item: any) => {
            const m = mapMatrixItem(item);
            if (m.capability_name) agentdata.custom.push(m);
          });
      }

      const hasDataFromMatrix =
        agentdata.embedded.length > 0 ||
        agentdata.extended.length > 0 ||
        agentdata.custom.length > 0;

      // Priority 2 — agent.embedded[] with implementation_type discrimination (fallback only)
      if (!hasDataFromMatrix) {
        const embeddedArray = agent?.embedded;
        if (Array.isArray(embeddedArray)) {
          embeddedArray.forEach((item: any) => {
            const type = item?.implementation_type || '';
            const name = item?.agent_name || '';
            if (!name) return;
            const newItem: CardData = {
              capability_name: name,
              description: item?.justification || '',
            };
            if (type === 'Extended') agentdata.extended.push(newItem);
            else if (type === 'Embedded') agentdata.embedded.push(newItem);
            else if (type === 'Client Specific') agentdata.custom.push(newItem);
          });
        }

        // Priority 3 — direct agent arrays (final fallback)
        if (agentdata.embedded.length === 0 && Array.isArray(agent?.embedded))
          agent.embedded.forEach((item: any) => {
            if (item?.agent_name)
              agentdata.embedded.push({
                capability_name: item.agent_name,
                description: item.justification || '',
              });
          });

        if (agentdata.extended.length === 0 && Array.isArray(agent?.extended))
          agent.extended.forEach((item: any) => {
            const name = item.agent_name || item.capability_name;
            if (name)
              agentdata.extended.push({
                capability_name: name,
                description: item.justification || item.description || '',
              });
          });

        if (agentdata.custom.length === 0 && Array.isArray(agent?.custom))
          agent.custom.forEach((item: any) => {
            const name = item.agent_name || item.capability_name;
            if (name)
              agentdata.custom.push({
                capability_name: name,
                description: item.justification || item.description || '',
              });
          });
      }

      const hasFinalData =
        agentdata.embedded.length > 0 ||
        agentdata.extended.length > 0 ||
        agentdata.custom.length > 0;

      if (!hasFinalData && agent) {
        console.warn('⚠️ Normalization returned empty arrays. Agent structure:', {
          hasEnablementMatrix: !!enablementMatrix,
          hasEmbeddedArray: Array.isArray(agent.embedded) && agent.embedded.length > 0,
          agentKeys: Object.keys(agent),
        });
      }

      return agentdata;
    }

    return DEFAULT_AGENT_DATA;
  } catch (error) {
    console.error('Error normalizing AI solution data:', error);
    return DEFAULT_AGENT_DATA;
  }
};
